import {
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import type {ActionMeta, GroupBase, SelectInstance} from 'react-select';
import {SelectOption as SelectOptionType, useTimeoutRef} from '@chekin/core';
import type {SelectProps} from './types';
import {customFilter, isGroup, isSingleValue} from './utils';
import {ReactSelect} from './styled';
import {
  SelectControl,
  SelectDropdownIndicator,
  SelectInput,
  SelectLoadingIndicator,
  SelectMenu,
  SelectMenuList,
  SelectPlaceholder,
  SelectOption,
} from './components';

type SelectRefType<T = undefined, V = string, L = string> = SelectInstance<
  SelectOptionType<T, V, L>,
  false,
  GroupBase<SelectOptionType<T, V, L>>
>;

export type {SelectProps} from './types';

function SelectInner<T, V, L>(
  {
    onChange,
    label,
    invalid,
    error,
    className,
    disabled,
    loading,
    components,
    onMenuOpen,
    onMenuClose,
    empty,
    onHandleKeyDown,
    ...props
  }: SelectProps<T, V, L>,
  ref: ForwardedRef<SelectRefType<T, V, L>>,
) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isSearchableEnabled = (props.options?.length || 0) > 15;
  const needShowScrollbar = (props.options?.length || 0) > 8;
  const {mobileVersion} = props;
  const focusTimeoutRef = useTimeoutRef();
  const selectRef = useRef<SelectRefType<T, V, L>>(null);
  const handleChange = (
    option: SelectOptionType<T, V, L> | null,
    info: ActionMeta<SelectOptionType<T, V, L>>,
  ) => {
    if (disabled) return;
    onChange?.(option, info);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (disabled) return;
    onHandleKeyDown?.(event);
  };

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    onMenuClose?.();
  }, [onMenuClose]);

  useImperativeHandle(ref, () => selectRef.current as SelectRefType<T, V, L>, []);

  useEffect(
    function autoFocusAfterTimeout() {
      if (!mobileVersion) return;
      focusTimeoutRef.current = setTimeout(() => {
        selectRef.current?.focus();
        clearTimeout(focusTimeoutRef.current);
      }, 400);
    },
    [focusTimeoutRef, mobileVersion],
  );

  useEffect(
    function setCorrectOptionIfThereIsOnlyValue() {
      const value = props.value;
      if (
        isSingleValue(value) &&
        value?.value &&
        value?.label === '' &&
        props.options?.length
      ) {
        const validOption = props.options.find(option => {
          if (isGroup(option)) return false;
          return option.value === value.value;
        });

        if (onChange && validOption && !isGroup(validOption)) {
          onChange(validOption, {action: 'select-option', option: validOption});
        }
      }
    },
    [onChange, props.options, props.value],
  );

  return (
    <ReactSelect
      ref={selectRef}
      empty={empty || !props.value}
      className={clsx(className, 'select', {
        'select--invalid': invalid || Boolean(error),
      })}
      classNamePrefix="select"
      error={error}
      label={label}
      invalid={invalid}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      isDisabled={disabled}
      isLoading={loading}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      isMenuOpen={isMenuOpen}
      filterOption={customFilter}
      showScrollbar={needShowScrollbar}
      isSearchable={isSearchableEnabled}
      components={{
        Menu: SelectMenu,
        Input: SelectInput,
        Option: SelectOption,
        Control: SelectControl,
        MenuList: SelectMenuList,
        Placeholder: SelectPlaceholder,
        LoadingIndicator: SelectLoadingIndicator,
        DropdownIndicator: SelectDropdownIndicator,
        IndicatorSeparator: null,
        ...components,
      }}
      {...props}
    />
  );
}

type SelectForwardType = <T = undefined, V = string, L = string>(
  props: SelectProps<T, V, L> & {ref?: ForwardedRef<SelectRefType<T, V, L>>},
  // @ts-expect-error ts(2577)
) => ReturnType<typeof Select>;

const Select = forwardRef(SelectInner) as SelectForwardType;

export {Select};
export type {SelectRefType, SelectForwardType};
